import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { TrackLyrics } from '@karozik/shared';
import { cn } from '@karozik/ui';

/** Lines show up slightly early, so the room reads them before singing. */
const LEAD_MS = 300;

/** Index of the line being sung at `ms`, -1 before the first one. */
export function lineAt(lines: { t: number }[], ms: number): number {
  let lo = 0;
  let hi = lines.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (lines[mid]!.t <= ms) {
      found = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return found;
}

/**
 * Time-synced lyrics under the artwork: the whole text scrolls so the current
 * line stays in the middle, the others fade out above and below.
 * `position` reads the player's playback position (ms in the track).
 */
export function LyricsPanel({ lyrics, position }: { lyrics: TrackLyrics; position: () => number }) {
  const lines = lyrics.synced ?? [];
  const [index, setIndex] = useState(-1);
  const [offset, setOffset] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const refs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const tick = () => setIndex(lineAt(lines, position() + LEAD_MS));
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [lines, position]);

  useLayoutEffect(() => {
    const el = refs.current[Math.max(0, index)];
    if (!el || !box.current) return;
    setOffset(box.current.clientHeight / 2 - (el.offsetTop + el.offsetHeight / 2));
  }, [index, lines]);

  return (
    <div
      ref={box}
      className="relative mt-[4vh] h-[26vh] overflow-hidden"
      style={{ maskImage: 'linear-gradient(transparent, black 30%, black 70%, transparent)' }}
    >
      {/* Positioned, so each line's offsetTop is measured from the list itself. */}
      <ol className="relative transition-transform duration-500 ease-out" style={{ transform: `translateY(${offset}px)` }}>
        {lines.map((l, i) => (
          <li
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={cn(
              'origin-left py-[0.6vh] text-[4.2vh] leading-tight font-bold transition-[opacity,transform,color] duration-500',
              i === index ? 'scale-100 text-ink opacity-100' : 'scale-[0.82] text-ink-dim',
              i !== index && (Math.abs(i - index) === 1 ? 'opacity-60' : 'opacity-25'),
            )}
          >
            {l.text || '♪'}
          </li>
        ))}
      </ol>
    </div>
  );
}
